import {
  getFirestore, collection, addDoc, getDocs, deleteDoc, doc,
  updateDoc, getDoc, serverTimestamp, onSnapshot
} from "firebase/firestore";
import { initializeApp } from "firebase/app"; // <-- REQUIRED (fixes your error)
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { useEffect, useState } from "react";
// ✅ Firebase Configuration
const firebaseConfig = {
  apiKey: "AIzaSyBuGlLEn5AK95FlHX9OAW5c7DAwXwpBMMQ",
  authDomain: "urbanclapclone-8dc68.firebaseapp.com",
  projectId: "urbanclapclone-8dc68",
  storageBucket: 'urbanclapclone-8dc68.firebasestorage.app', // ✅ Use correct bucket,
  messagingSenderId: "891430644958",
  appId: "1:891430644958:web:4be7e03968b497c8ace64f",
  measurementId: "G-TVPCNE1P0V"
};
// ✅ Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);
// ✅ Messaging Initialization (Browser only)
let messaging = null;
isSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
  } else {
    console.warn("⚠️ Firebase Messaging not supported in this environment.");
  }
});
// ✅ Collection Refs
const usersCollection = collection(db, "users");
const transactionsCollection = collection(db, "creditTransactions");
const discountCouponsCollection = collection(db, "discountCoupons");
// ✅ Fetch All Documents
const fetchCollectionData = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("🔥 Error fetching collection data:", error);
    throw error;
  }
};
// ✅ Add Document
const addDocumentToFirestore = async (collectionName, documentData) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...documentData,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error("🔥 Error adding document:", error);
    throw error;
  }
};
// ✅ Get Single Document
const getDocumentFromFirestore = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    console.error("🔥 Error fetching document:", error);
    throw error;
  }
};
// ✅ Update Document
const updateDocumentInFirestore = async (collectionName, docId, updatedData) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, updatedData);
    console.log(`✅ Document with ID ${docId} updated successfully.`);
  } catch (error) {
    console.error("🔥 Error updating document:", error);
    throw error;
  }
};
// ✅ Delete Document
const deleteDocumentFromFirestore = async (collectionName, docId) => {
  try {
    await deleteDoc(doc(db, collectionName, docId));
    console.log(`✅ Document with ID ${docId} deleted successfully.`);
  } catch (error) {
    console.error("🔥 Error deleting document:", error);
    throw error;
  }
};
// ✅ Upload File
const uploadFileToStorage = async (file, storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("🔥 Error uploading file:", error);
    throw error;
  }
};

// ✅ Realtime Credit Transactions
const useCreditTransactions = () => {
  const [creditTransactions, setCreditTransactions] = useState([]);
  useEffect(() => {
    const unsubscribe = onSnapshot(transactionsCollection, (querySnapshot) => {
      const transactions = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCreditTransactions(transactions);
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, []);
  return creditTransactions;
};
// ✅ Add Wallet Transaction (User-specific and Global)
const addWalletTransaction = async (amount, message = "Transaction", status = "pending") => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("❌ User must be logged in!");
    const userId = user.uid;
    const transactionData = {
      userId,
      amount,
      message,
      status,
      currency: "USD",
      timestamp: serverTimestamp(),
    };

    console.log("🔄 Adding wallet transaction...", transactionData);
    await Promise.all([
      addDoc(collection(db, `users/${userId}/walletTransactions`), transactionData),
      addDoc(collection(db, "walletTransactions"), transactionData),
    ]);

    console.log("✅ Wallet transaction added successfully!");
  } catch (error) {
    console.error("🔥 Error adding wallet transaction:", error);
  }
};
// ✅ Delete Transaction
const deleteTransaction = async (transactionId) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("❌ User must be logged in!");
    await deleteDoc(doc(db, "walletTransactions", transactionId));
    console.log("✅ Transaction deleted successfully.");
  } catch (error) {
    console.error("🔥 Error deleting transaction:", error);
  }
};

// ✅ Add Discount Coupon
const addDiscountCoupon = async (discountName, discountStatus, discountAmount) => {
  try {
    const couponData = {
      discountName,
      discountStatus,
      discountAmount: parseFloat(discountAmount),
      timestamp: serverTimestamp(),
    };

    console.log("🔄 Adding discount coupon...", couponData);
    await addDoc(discountCouponsCollection, couponData);
    console.log("✅ Discount coupon added successfully!");
  } catch (error) {
    console.error("🔥 Error adding discount coupon:", error);
  }
};

// ✅ Realtime Discount Coupons
const useDiscountCoupons = () => {
  const [discountCoupons, setDiscountCoupons] = useState([]);
  useEffect(() => {
    const unsubscribe = onSnapshot(discountCouponsCollection, (querySnapshot) => {
      const coupons = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDiscountCoupons(coupons);
    });
    return () => unsubscribe();
  }, []);

  return discountCoupons;
};
// ✅ Request Notification Permission + Token
const requestNotificationPermissionAndToken = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("❌ Notification permission not granted.");
      return null;
    }
    if (!messaging) {
      console.warn("❌ Messaging is not supported or not initialized.");
      return null;
    }
    const currentToken = await getToken(messaging, {
      vapidKey: "BGJWyVrh6Zgxb3fkYOQztkajgIJpL595pMl9y08ikhx4YO-HL47xcjcUNbdamgBX-D23OzxoYt0GjKUWrzFFoTw"
    });
    if (!currentToken) {
      console.warn("⚠️ No valid FCM token retrieved.");
      return null;
    }
    console.log("✅ FCM Token:", currentToken);
    return currentToken;
  } catch (error) {
    console.error("🔥 Error getting FCM token:", error.message);
    return null;
  }
};
// ✅ Foreground Message Handler
const onForegroundMessage = (callback) => {
  if (!messaging) return;
  onMessage(messaging, (payload) => {
    console.log("📩 Foreground message received:", payload);
    callback(payload);
  });
};
// ✅ Create a promise that resolves with messaging if supported
let messagingPromise = isSupported().then((supported) => {
  if (supported) {
    return getMessaging(app);
  } else {
    console.warn("⚠️ Firebase Messaging not supported in this environment.");
    return null;
  }
});
// ✅ Export the promise
export const messagingPromiseInstance = messagingPromise;
// ✅ Exports
export {
  db, auth, storage,
  usersCollection, transactionsCollection,
  fetchCollectionData, addDocumentToFirestore,
  getDocumentFromFirestore, updateDocumentInFirestore,
  deleteDocumentFromFirestore, uploadFileToStorage,
  useCreditTransactions, addWalletTransaction,
  deleteTransaction, addDiscountCoupon,
  useDiscountCoupons, requestNotificationPermissionAndToken,
  onForegroundMessage
};